// Die Ablage (02.11.2026, Skizze „Ablage und Titel-Blatt", vom Betreiber
// freigegeben).
//
// DER BEFUND: Am PC fehlten „Tabs" — man arbeitet an einer Bestellung,
// muss kurz eine andere nachsehen, und findet den Weg zurück nicht. Die
// installierte App kann keine Browser-Tabs (E49). Die Ablage parkt eine
// Seite als Pille am unteren Rand; ein Tipp holt sie zurück.
//
// WAS GESPEICHERT WIRD: nur die ADRESSE der Seite, je Gerät, höchstens
// sechs. Kein Name, kein Titel — die Pille holt ihre Beschriftung beim
// Anzeigen frisch (Ablage.tsx). So liegt im Browserspeicher keine
// Kundin, nur eine Bestellnummer oder ein Suchbegriff, wie in der
// Adresszeile auch. localStorage ist für solche Ansichts-Vorlieben
// erlaubt (CLAUDE.md), nicht für Anmeldedaten.

export const ABLAGE_HOECHSTENS = 6;
const SCHLUESSEL = 'tracking:ablage:v1';
const EREIGNIS = 'tracking:ablage';

export type AblageArt = 'bestellung' | 'dashboard' | 'archiv' | 'reserviert' | 'kanban';

/** Was eine Adresse für die Ablage ist — oder null, wenn sie nicht ablegbar ist. */
export function ablageArt(pfad: string): { art: AblageArt; nr?: string; q?: string; quelle?: string } | null {
  const [weg, such = ''] = pfad.split('?');
  const p = new URLSearchParams(such);
  const q = p.get('q') ?? undefined;
  const m = /^\/bestellungen\/([^/]+)$/.exec(weg);
  if (m) return { art: 'bestellung', nr: decodeURIComponent(m[1]) };
  if (weg === '/' || weg === '') return { art: 'dashboard', q, quelle: p.get('quelle') ?? undefined };
  if (weg === '/archiv') return { art: 'archiv', q };
  if (weg === '/reserviert') return { art: 'reserviert', q };
  if (weg === '/kanban') return { art: 'kanban' };
  return null;
}

/**
 * Die Adresse, wie sie abgelegt wird: ohne die Schalter, die ein Blatt
 * über der Seite öffnen (`neu`, `reserviert`) — abgelegt wird die Seite,
 * nicht der Dialog darauf.
 */
export function ablagePfad(pathname: string, searchStr: string): string {
  const p = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr);
  p.delete('neu');
  p.delete('reserviert');
  const rest = p.toString();
  return rest === '' ? pathname : `${pathname}?${rest}`;
}

export function ablageLesen(): string[] {
  try {
    const roh = window.localStorage.getItem(SCHLUESSEL);
    const liste: unknown = roh === null ? [] : JSON.parse(roh);
    return Array.isArray(liste)
      ? liste.filter((x): x is string => typeof x === 'string' && ablageArt(x) !== null).slice(0, ABLAGE_HOECHSTENS)
      : [];
  } catch {
    return [];
  }
}

function schreiben(liste: string[]): void {
  try {
    window.localStorage.setItem(SCHLUESSEL, JSON.stringify(liste));
  } catch {
    // Privates Fenster o. ä.: Die Ablage gilt dann nur bis zum Neuladen.
  }
  zwischen = liste;
  window.dispatchEvent(new Event(EREIGNIS));
}

let zwischen: string[] | null = null;

/** Für useSyncExternalStore: dieselbe Liste, solange sich nichts ändert. */
export function ablageStand(): string[] {
  if (zwischen === null) zwischen = ablageLesen();
  return zwischen;
}

export function ablageAbonnieren(neu: () => void): () => void {
  const andererTab = (e: StorageEvent): void => {
    if (e.key === SCHLUESSEL) {
      zwischen = null;
      neu();
    }
  };
  window.addEventListener(EREIGNIS, neu);
  window.addEventListener('storage', andererTab);
  return () => {
    window.removeEventListener(EREIGNIS, neu);
    window.removeEventListener('storage', andererTab);
  };
}

/** Ablegen. `false`, wenn die Ablage voll ist — dann wird nichts verdrängt. */
export function ablegen(pfad: string): boolean {
  const liste = ablageStand();
  if (liste.includes(pfad)) return true;
  if (liste.length >= ABLAGE_HOECHSTENS) return false;
  schreiben([...liste, pfad]);
  return true;
}

export function ablageEntfernen(pfad: string): void {
  schreiben(ablageStand().filter((p) => p !== pfad));
}

/** Nur für Tests. */
export function ablageZuruecksetzen(): void {
  zwischen = null;
  try {
    window.localStorage.removeItem(SCHLUESSEL);
  } catch {
    // nichts
  }
}
